import { fromBase64Url, toBase64Url, utf8 } from '../encoding';
import { signBytes, verifySignature } from '../signatures';
import type { Bundle, Coding, Composition, Signature } from './types';

/** The signature type FHIR uses for the author of a document. */
export const AUTHOR_SIGNATURE: Coding = { system: 'urn:iso-astm:E1762-95:2013', code: '1.2.840.10065.1.12.1.1', display: "Author's Signature" };
/** Signature data is a JSON Web Signature with a detached payload. */
export const DOCUMENT_SIGNATURE_FORMAT = 'application/jose';

const isObject = (value: unknown): value is Record<string, unknown> => typeof value === 'object' && value !== null && !Array.isArray(value);

function canonical(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonical).join(',')}]`;
  if (isObject(value)) {
    const keys = Object.keys(value).sort();
    return `{${keys.map((key) => `${JSON.stringify(key)}:${canonical(value[key])}`).join(',')}}`;
  }
  return JSON.stringify(value);
}

/**
 * JSON with object keys sorted and no spaces, as in RFC 8785. The value goes
 * through JSON first, so a document signs the same before and after it
 * travels as JSON.
 */
export function canonicalJson(value: unknown): string {
  return canonical(JSON.parse(JSON.stringify(value)));
}

const toBase64 = (bytes: Uint8Array) => {
  const url = toBase64Url(bytes).replace(/-/g, '+').replace(/_/g, '/');
  return url + '='.repeat((4 - (url.length % 4)) % 4);
};

function fromBase64(text: string): Uint8Array<ArrayBuffer> {
  if (!/^[A-Za-z0-9+/]*={0,2}$/.test(text)) throw new Error('Invalid base64 text');
  return fromBase64Url(text.replace(/=+$/, '').replace(/\+/g, '-').replace(/\//g, '_'));
}

/**
 * What the signature covers: the whole bundle, including the signature's own
 * type, time and signer, with only its data left out.
 */
function signedPayload(bundle: Bundle, signature: Omit<Signature, 'data'>): Uint8Array<ArrayBuffer> {
  return utf8(canonicalJson({ ...bundle, signature }));
}

const signingInput = (header: string, payload: Uint8Array) => utf8(`${header}.${toBase64Url(payload)}`);

/**
 * Signs a document as its author: an ES256 JWS over the canonical bundle,
 * with the signing device's ID as the key ID. The device ID is the
 * fingerprint printed on the prescription, and the key directory gives its
 * public key to anyone who checks. Any change to the bundle breaks it.
 */
export async function signDocument(bundle: Bundle, signer: { deviceId: string; signingKey: CryptoKey }, when = new Date()): Promise<Bundle> {
  const composition = bundle.entry?.[0]?.resource;
  const who = composition?.resourceType === 'Composition' ? (composition as Composition).author[0] : undefined;
  if (!who?.reference) throw new Error('A document needs an author to sign it');
  const { signature: _old, ...unsigned } = bundle;
  const meta: Omit<Signature, 'data'> = {
    type: [AUTHOR_SIGNATURE],
    when: when.toISOString(),
    who: { reference: who.reference },
    sigFormat: DOCUMENT_SIGNATURE_FORMAT,
  };
  const header = toBase64Url(utf8(JSON.stringify({ alg: 'ES256', kid: signer.deviceId })));
  const signature = await signBytes(signer.signingKey, signingInput(header, signedPayload(unsigned, meta)));
  return { ...unsigned, signature: { ...meta, data: toBase64(utf8(`${header}..${toBase64Url(signature)}`)) } };
}

interface ParsedSignature {
  deviceId: string;
  when: string;
  header: string;
  signature: Uint8Array<ArrayBuffer>;
  meta: Omit<Signature, 'data'>;
}

function parse(bundle: Bundle): ParsedSignature | null {
  const sig = bundle.signature;
  if (!isObject(sig) || sig.sigFormat !== DOCUMENT_SIGNATURE_FORMAT || typeof sig.data !== 'string' || typeof sig.when !== 'string') return null;
  try {
    const jws = new TextDecoder('utf-8', { fatal: true }).decode(fromBase64(sig.data));
    const [header, payload, signature, ...rest] = jws.split('.');
    if (header === undefined || payload !== '' || signature === undefined || rest.length > 0) return null;
    const fields: unknown = JSON.parse(new TextDecoder('utf-8', { fatal: true }).decode(fromBase64Url(header)));
    if (!isObject(fields) || fields.alg !== 'ES256' || typeof fields.kid !== 'string' || Object.keys(fields).length !== 2) return null;
    const { data: _data, ...meta } = sig;
    return { deviceId: fields.kid, when: sig.when, header, signature: fromBase64Url(signature), meta };
  } catch {
    return null;
  }
}

/** Who signed a document and when, read without checking. Null when it has no signature this app can read. */
export function documentSigner(bundle: Bundle): { deviceId: string; when: string } | null {
  const parsed = parse(bundle);
  return parsed ? { deviceId: parsed.deviceId, when: parsed.when } : null;
}

/**
 * True when the document carries a valid signature by this public key. The
 * caller fetches the key for documentSigner's device ID and checks that the
 * device belongs to someone who may sign it, such as a doctor.
 */
export async function verifyDocumentSignature(bundle: Bundle, signingKeyRaw: Uint8Array): Promise<boolean> {
  const parsed = parse(bundle);
  if (!parsed) return false;
  const { signature: _sig, ...unsigned } = bundle;
  return verifySignature(signingKeyRaw, parsed.signature, signingInput(parsed.header, signedPayload(unsigned, parsed.meta)));
}
